import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { type Loan, lendingDisbursementsCreateDisbursementMutation } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button, Input, MoneyInput, Switch, formatAmount } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { useLoan } from '~/components/fields/LoanDropdown';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { getErrorMessage, invalidateLoanBalances } from '~/lib/api';
import {
  BankAccountSelector,
  LoanSelector,
  type SelectedCardRow,
  formatStatus,
  getAvailablePrincipal,
  loanIdRow,
} from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

// Loans are USD-only today; once a loan is selected its currency is used instead.
const DEFAULT_LOAN_CURRENCY = 'USD';

type CreateLoanDisbursementForm = {
  description: string;
  loanId: string;
  bankAccountId: string;
  amount?: number;
  hold: boolean;
};

interface Params {
  id?: string;
}

const isPositive = (value?: number): value is number => typeof value === 'number' && value > 0;

const disbursementRows = (loan: Loan): SelectedCardRow[] => [
  {
    label: 'Available to disburse',
    value: formatAmount(getAvailablePrincipal(loan), loan.currency),
    emphasized: true,
  },
  { label: 'Max principal balance', value: formatAmount(loan.max_principal_balance, loan.currency) },
  { label: 'Status', value: formatStatus(loan.status) },
  loanIdRow(loan),
];

const availableBalance = (loan: Loan) => ({ amount: getAvailablePrincipal(loan), label: 'Available' });

export const PageLoansDisbursementCreate: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleSubmitErrors } = useFormErrorHandler();
  const { id } = useParams<keyof Params>() as Params;
  const canWrite = useSessionStore((state) => state.isAuthorized({ permission: 'loans', permissionLevel: 'write' }));
  const returnRoute = id ? `${ROUTE.LOANS}/edit/${id}` : `${ROUTE.LOANS}/disbursements`;

  const formMethods = useForm<CreateLoanDisbursementForm>({
    defaultValues: {
      description: '',
      loanId: id ?? '',
      bankAccountId: '',
      hold: false,
    },
  });

  const { handleSubmit, watch } = formMethods;
  const loanId = watch('loanId');
  const { data: loan } = useLoan(loanId || undefined);
  const currency = loan?.currency ?? DEFAULT_LOAN_CURRENCY;

  const createDisbursement = useMutation({
    ...lendingDisbursementsCreateDisbursementMutation(),
    onSuccess: (disbursement, { body }) => {
      invalidateLoanBalances(queryClient, body.loan_id);
      navigate(`${ROUTE.LOANS}/edit/${body.loan_id}`);
      addSuccessNotification({ content: `${disbursement.description} added` });
    },
    onError: (error) => addDangerNotification({ content: getErrorMessage(error) }),
  });

  const handleCreate = (data: CreateLoanDisbursementForm) =>
    createDisbursement.mutate({
      body: {
        description: data.description,
        loan_id: data.loanId,
        bank_account_id: data.bankAccountId,
        amount: data.amount ?? 0,
        currency,
        hold: data.hold,
      },
    });

  const cancelButton = (
    <Button onClick={() => navigate(returnRoute)} variant="secondary" size="sm" type="button">
      Cancel
    </Button>
  );
  const createButton = (
    <Button
      onClick={handleSubmit(handleCreate, handleSubmitErrors)}
      disabled={!canWrite || createDisbursement.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus />
      {createDisbursement.isPending ? 'Creating…' : 'Create Disbursement'}
    </Button>
  );

  return (
    <>
      <PageActions>
        {cancelButton}
        {createButton}
      </PageActions>

      {!canWrite && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleCreate, handleSubmitErrors)}>
          {RenderFields<CreateLoanDisbursementForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'description',
                    label: 'Description',
                    rules: { required: true, maxLength: { value: 127, message: 'Description is too long' } },
                    children: ({ value, onChange, onBlur, ref }, { error }) => (
                      <Input
                        ref={ref}
                        placeholder="Description"
                        aria-invalid={!!error}
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    ),
                  },
                  {
                    id: 'loanId',
                    label: 'Loan',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <LoanSelector
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        locked={Boolean(id)}
                        hasError={!!error}
                        getRows={disbursementRows}
                        getBalance={availableBalance}
                      />
                    ),
                  },
                  {
                    id: 'bankAccountId',
                    label: 'Bank Account',
                    description: 'The account the loan funds are disbursed into.',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <BankAccountSelector
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        hasError={!!error}
                      />
                    ),
                  },
                  {
                    id: 'amount',
                    label: 'Amount',
                    rules: {
                      required: true,
                      validate: (value) => {
                        const v = value as number | undefined;
                        if (!isPositive(v)) {
                          return 'Amount must be greater than zero';
                        }
                        // The API rejects disbursements that would take the loan past its max principal.
                        if (loan && v > getAvailablePrincipal(loan)) {
                          return `Amount exceeds the ${formatAmount(getAvailablePrincipal(loan), loan.currency)} available on this loan`;
                        }
                        return true;
                      },
                    },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <MoneyInput
                        currency={currency}
                        value={typeof value === 'number' ? value : undefined}
                        onValueChange={onChange}
                        onBlur={onBlur}
                        aria-invalid={!!error}
                      />
                    ),
                  },
                  {
                    id: 'hold',
                    label: 'Hold',
                    description: 'Creates the disbursement on hold. It completes once the hold is cleared.',
                    children: ({ value, onChange }) => (
                      <Switch className="@tablet/form-field:mt-3" checked={Boolean(value)} onCheckedChange={onChange} />
                    ),
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <div className="flex items-start justify-between px-(--page-margin) py-6">
        {cancelButton}
        {createButton}
      </div>
    </>
  );
};
